import { useAppSelector } from 'hooks/redux';
import { Map as ImmutableMap } from 'immutable';
import type { SyntheticEvent } from 'react';
import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import DeleteShareIcon from '@mui/icons-material/CallMissedOutgoing';
import SwitchDatasetIcon from '@mui/icons-material/KeyboardTab';
import ThreeDotIcon from '@mui/icons-material/MoreVert';

import { authActions, authSelectors, authTypes } from 'app-components/flux/auth';
import { actionHelpers } from 'app-components/flux/core';
import { datasetsSelectors, datasetsUtils } from 'app-components/flux/datasets';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { sharesActions } from 'app-components/flux/shares';
import { getLogger } from 'app-components/utils/core';

import type { Dataset } from 'app-components/flux/datasets/datasetsTypes';
import {
  DIALOG_TYPE as CONFIRMATION_DIALOG_TYPE,
  mkConfirmationDialogProps,
} from 'components/Dialogs/ConfirmationDialog';

const log = getLogger('SharedDatasetMenu');

type DatasetShares = {
  id: string;
  datasetId: string;
  granteeFirstName: string;
  granteeLastName: string;
  granteeEmail: string;
  grantorFirstName: string;
  grantorLastName: string;
  grantorEmail: string;
  permissions: string;
  state: string;
  lastAccessedAt: string;
  testMode: boolean;
  createdAt: string;
};

const SharedDatasetMenu = ({ dataset, shares }: { dataset: Dataset; shares: DatasetShares[] | undefined }) => {
  const dispatch = useDispatch();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | Element>(null);

  const activeDatasetId = useAppSelector(authSelectors.getDatasetId);
  const datasetIsActive = dataset.id === activeDatasetId;

  // get another dataset to switch to, it must have entitlements,
  // if there are none assume the user does not have a subscription
  const firstValidDataset = useAppSelector((state) =>
    datasetsSelectors
      .getDatasets(state)
      ?.filter((dataset: Dataset) => dataset.id !== activeDatasetId && dataset.entitlements?.length)
      .valueSeq()
      .first(),
  );

  const hasActiveEntitlement = useMemo(() => datasetsUtils.hasActiveEntitlement(dataset), [dataset]);

  // on a dataset not owned by user, there is only one share (the users share)
  const share = shares && shares[0];

  const handleOpenMenu = (event: SyntheticEvent) => setMenuAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setMenuAnchorEl(null);

  const handleLeaveShare = () => {
    handleCloseMenu();
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'delete-share',
          type: CONFIRMATION_DIALOG_TYPE,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Leave this space',
              content: 'After leaving, you will no longer have access to this space.',
              confirmLabel: 'Continue',
              denyLabel: 'Cancel',
              confirmAction: () => {
                const deleteSharePromise = actionHelpers.bindPromiseAction(dispatch, sharesActions.deleteShare);
                deleteSharePromise(share)
                  .then(
                    () => {
                      if (datasetIsActive) {
                        if (firstValidDataset) {
                          dispatch(
                            authActions.authSelectDataset(
                              authTypes.AuthSelectDatasetPayload({ dataset: firstValidDataset }),
                            ),
                          );
                        } else {
                          //no sub
                          dispatch(
                            authActions.authLogout(
                              { reason: 'left only authorized space, no sub' },
                              { context: 'auth dialog: logout manually' },
                            ),
                          );
                        }
                      }
                    },
                    () => {
                      log.error('Deleting Share Failed');
                      // TODO: What to do in case of failure?
                    },
                  )
                  .catch((e) => assert(false, e));
              },
            }),
          ),
        }),
      ),
    );
  };

  const handleSelectDataset = () => {
    handleCloseMenu();
    dispatch(authActions.authSelectDataset(authTypes.AuthSelectDatasetPayload({ dataset })));
  };

  const renderMenu = () => (
    <div>
      <IconButton
        id={`dataset-menu-button-${dataset.id}`}
        onClick={handleOpenMenu}
        size='large'
        disabled={!hasActiveEntitlement}
      >
        <ThreeDotIcon />
      </IconButton>
      <Menu
        id={`dataset-menu-${dataset.id}`}
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem disabled={dataset.id === activeDatasetId} onClick={handleSelectDataset}>
          <ListItemIcon>
            <SwitchDatasetIcon fontSize='small' />
          </ListItemIcon>
          Switch to this space
        </MenuItem>
        {share && [
          <Divider key={`divider-${share.id}`} />,
          <MenuItem key={`menuitem-${share.id}`} onClick={handleLeaveShare}>
            <ListItemIcon>
              <DeleteShareIcon fontSize='small' />
            </ListItemIcon>
            <Typography sx={{ maxWidth: 200 }} variant='inherit' noWrap>
              Leave this space
            </Typography>
          </MenuItem>,
        ]}
      </Menu>
    </div>
  );

  return renderMenu();
};

export default SharedDatasetMenu;
